import type { AccordionDesignTokens } from '@primeuix/themes/types/accordion';

/**
 * Accordion component tokens
 *
 * Panels are separated by a 2px gap in the page background colour rather than a
 * divider line, and only the outer corners of the accordion are rounded.
 *
 * Headers stick to the top of their scroll container when the accordion has the
 * `p-accordion-sticky-headers` class. Set `--accordion-sticky-offset` on an
 * ancestor to keep them below another sticky element.
 *
 * Reference implementation:
 * https://github.com/primefaces/primeuix/blob/main/packages/themes/src/presets/aura/accordion/index.ts
 *
 * Component documentation:
 * https://primeng.org/accordion
 */
export const accordionComponentTokens: Partial<AccordionDesignTokens> = {
  panel: {
    borderWidth: '0 0 2px 0',
  },
  header: {
    padding: '1rem',
    fontWeight: '600',
    borderRadius: '0',
    borderWidth: '0',
    first: {
      topBorderRadius: '{border.radius.md}',
      borderWidth: '0',
    },
    last: {
      bottomBorderRadius: '{border.radius.md}',
      activeBottomBorderRadius: '0',
    },
  },
  content: {
    borderWidth: '0',
  },
  colorScheme: {
    light: {
      panel: {
        borderColor: '{content.background}',
      },
      header: {
        color: '{surface.700}',
        hoverColor: '{surface.700}',
        activeColor: '{surface.700}',
        activeHoverColor: '{surface.700}',
        background: '{surface.50}',
        hoverBackground: '{surface.100}',
        activeBackground: '{surface.50}',
        activeHoverBackground: '{surface.100}',
        toggleIcon: {
          color: '{surface.700}',
          hoverColor: '{surface.700}',
          activeColor: '{surface.700}',
          activeHoverColor: '{surface.700}',
        },
      },
      content: {
        background: '{surface.50}',
      },
    },
  },
  css: ({ dt }) => `
    .p-accordionpanel:last-child .p-accordioncontent-content {
      border-end-start-radius: ${dt('accordion.header.last.bottom.border.radius')};
      border-end-end-radius: ${dt('accordion.header.last.bottom.border.radius')};
    }

    .p-accordion-sticky-headers .p-accordionheader {
      position: sticky;
      top: var(--accordion-sticky-offset, 0);
      z-index: 1;
    }
  `,
};
